import { useRouterState, useSearch } from "@tanstack/react-router";

import { observer } from "mobx-react-lite";
import { cn } from "@synthesis/shared-utils";
import { useEffect, useMemo } from "react";

import { useGet4 } from "@synthesis/shared-services/internal/payment/hooks";
import { useClientType } from "../../../shared/hooks/use-client-type";
import { useUserRoles } from "../../../shared/hooks/use-user-roles";
import {
  canAddFinalDecisionVersion,
  canDeleteReviewDocuments,
  canDeleteTuParts,
  canEditFinalDecision,
  canManageFinalDecisionRemarks,
  canUploadReviewDocuments,
  canUploadTuParts,
  resolveApplicationDetailTabVisibilityContext,
  shouldShowFinalDecisionPlaceholder,
} from "../application-detail-tabs.lib";
import { isFinalDecisionVersionStatus } from "./final-decision-tab/final-decision.lib";
import { usePageStore, type ApplicationDetailTabKey } from "../application-detail-page.lib";
import { useApplicationDetail } from "../hooks/use-application-detail";
import { useFinalDecisionCurrentVersion, useFinalDecisionVersionHistory } from "../hooks/use-final-decision";
import { ApplicantContent } from "./applicant-tab/applicant-content";
import { ApplicantPayerContent } from "./payer-details-tab/applicant-payer-content";
import { CommentsContent } from "./comments-tab/comments-content";
import { ActionHistoryContent } from "./history-tab/action-history-content";
import { AttachedDocumentsContent } from "./documents-tab/attached-documents-content";
import { FinalDecisionContent } from "./final-decision-tab/final-decision-content";
import { CrossingObjectDescriptionContent } from "./object-description-tab/crossing-object-description-content";
import { ObjectDescriptionContent } from "./object-description-tab/object-description-content";
import { PaymentContent } from "./payment-tab/payment-content";
import { ReviewDocumentsContent } from "./review-documents-tab/review-documents-content";

const tabPanelClassName = (activeTab: ApplicationDetailTabKey, tab: ApplicationDetailTabKey) => cn(activeTab !== tab && "hidden");

export const ApplicationDetailTabContent = observer(() => {
  const pageStore = usePageStore();
  const clientType = useClientType();
  const roles = useUserRoles();
  const { application, isFinalDecisionDraftAccessDenied } = useApplicationDetail();
  const activeTab = pageStore.activeTab;
  const { tab } = useSearch({ from: "/applications/$id" });
  const locationKey = useRouterState({ select: (state) => state.location.state.key });

  const { data: paymentView } = useGet4(
    { path: { id: application?.requestId ?? "" } },
    {
      query: { enabled: clientType === "internal" && !!application?.requestId },
    },
  );

  const currentVersion = useFinalDecisionCurrentVersion(application?.requestId ?? "", {
    enabled: clientType === "internal" && Boolean(application?.requestId) && isFinalDecisionVersionStatus(application?.status),
  });

  const versionHistory = useFinalDecisionVersionHistory(application?.requestId ?? "", currentVersion.version?.versionNumber, {
    enabled: clientType === "internal" && Boolean(application?.requestId) && isFinalDecisionVersionStatus(application?.status),
  });

  useEffect(() => {
    pageStore.setPaymentCanApprove(paymentView?.canApprove === true);
  }, [paymentView, pageStore]);

  const tabVisibilityContext = useMemo(() => {
    if (!application || !clientType) {
      return null;
    }

    return resolveApplicationDetailTabVisibilityContext({
      clientType,
      status: application.status,
      roles,
      applicantData: application.applicantData,
      paymentTerms: application.paymentTermsValue,
      explicitPaidService: application.isPaidServiceRequired,
      hasFinalDecision: application.hasFinalDecision,
    });
  }, [application, clientType, roles]);

  useEffect(() => {
    if (application) {
      pageStore.setApplication(application);
    }
  }, [application, pageStore]);

  useEffect(() => {
    pageStore.setActiveTab(tab ?? "object-description");
  }, [pageStore, tab, locationKey]);

  if (!application || !clientType || !tabVisibilityContext) {
    return null;
  }

  const showFinalDecisionPlaceholder = shouldShowFinalDecisionPlaceholder({
    hasFinalDecision: application.hasFinalDecision,
    status: application.status,
  });

  return (
    <>
      <div className={tabPanelClassName(activeTab, "object-description")}>
        {application.crossingObjectData ? (
          <CrossingObjectDescriptionContent crossingObjectData={application.crossingObjectData} />
        ) : (
          <ObjectDescriptionContent adjacentObjectData={application.adjacentObjectData} />
        )}
      </div>
      <div className={tabPanelClassName(activeTab, "documents")}>
        <AttachedDocumentsContent documents={application.documents} />
      </div>
      <div className={tabPanelClassName(activeTab, "applicant")}>
        <ApplicantContent applicantData={application.applicantData} />
      </div>
      <div className={tabPanelClassName(activeTab, "payer-details")}>
        <ApplicantPayerContent applicantData={application.applicantData} />
      </div>
      <div className={tabPanelClassName(activeTab, "payment")}>
        <PaymentContent requestId={application.requestId} clientType={clientType} />
      </div>
      <div className={tabPanelClassName(activeTab, "review-documents")}>
        <ReviewDocumentsContent
          requestId={application.requestId}
          canUpload={canUploadReviewDocuments(tabVisibilityContext)}
          canDelete={canDeleteReviewDocuments(tabVisibilityContext)}
          canUploadTuParts={canUploadTuParts(tabVisibilityContext)}
          canDeleteTuParts={canDeleteTuParts(tabVisibilityContext)}
        />
      </div>
      <div className={tabPanelClassName(activeTab, "comments")}>
        <CommentsContent />
      </div>
      <div className={tabPanelClassName(activeTab, "final-decision")}>
        <FinalDecisionContent
          requestId={application.requestId}
          status={application.status}
          finalDecision={application.finalDecision}
          showPlaceholder={showFinalDecisionPlaceholder}
          canAddVersion={canAddFinalDecisionVersion(tabVisibilityContext)}
          canEditDecision={canEditFinalDecision(tabVisibilityContext)}
          currentVersion={currentVersion.version}
          currentVersionDocuments={currentVersion.documents}
          currentVersionRemarks={currentVersion.remarks}
          versionHistory={versionHistory.versions}
          canManageRemarks={canManageFinalDecisionRemarks(tabVisibilityContext)}
          isDraftAccessDenied={isFinalDecisionDraftAccessDenied}
        />
      </div>
      <div className={tabPanelClassName(activeTab, "history")}>
        <ActionHistoryContent applicationId={application?.requestId} />
      </div>
    </>
  );
});
